import React, {useCallback, useContext, useState, useEffect} from 'react';
import {useTranslator} from '@jutro/locale';
import {useDataRefresh, ViewModelForm, ViewModelServiceContext} from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';

import messages from './MLPolicyNamedInsuredCandidatesComponent.messages';
import metadata from './MLPolicyNamedInsuredCandidatesComponent.metadata.json5';
import styles from './MLPolicyNamedInsuredCandidatesComponent.module.scss';

function MLPolicyNamedInsuredCandidatesComponent(props) {
    const {
        submissionVM,
        initPublicID,
        setNamedInsured,
        namedInsuredCandidates,
        label,
        setContactSelected,
        availableContactSelected,
        loadNamedInsuredCandidates,
        isPrimaryNamedInsured,
        isNotfromAdditionalIntrestPage,
        isSecondaryNamedInsured
    } = props;

    const { refreshData } = useDataRefresh();

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();

    const [selectedInsuredCandidatePublicID, setSelectedInsuredCandidatePublicID] = useState(initPublicID);

    const [showInsuredCandidateDropdown, setShowInsuredCandidateDropdown] = useState(false);

    const accountContactDTOPath = 'edge.capabilities.policycommon.accountcontact.dto.AccountContactDTO';

    const createNewInsuredCandidate = useCallback((publicID, displayName) => {
        const newInsuredCandidate = {
            publicID,
            displayName
        };
        return viewModelService.create(newInsuredCandidate, 'pc', accountContactDTOPath);
    }, [viewModelService]);

    const filterAvailableInsuredCandidates = useCallback(() => {
        let list = [];
        if (namedInsuredCandidates) {
            list = namedInsuredCandidates.filter((insuredCandidate) => insuredCandidate !== undefined);
        }
         
        const newInsuredCandidateVMOption = createNewInsuredCandidate('new', translator(messages.AddNewInsuredCandidate));
        list.push(newInsuredCandidateVMOption.value);
        
        return list;
    }, [namedInsuredCandidates, createNewInsuredCandidate, translator]);

    const filteredAvailableInsuredCandidates = filterAvailableInsuredCandidates();


    const onAddInsuredCandidate = useCallback((value) => {

        if (setContactSelected && value) {
            setContactSelected();
        }

        setSelectedInsuredCandidatePublicID(value);

        let selectedInsuredCandidate;
        if (namedInsuredCandidates) {
            selectedInsuredCandidate = namedInsuredCandidates.find((insuredCandidate) => insuredCandidate.publicID === value);
        }
        if (selectedInsuredCandidate) {
            const newSelectedInsuredCandidate = viewModelService.create(selectedInsuredCandidate, 'pc', accountContactDTOPath);
            setNamedInsured(newSelectedInsuredCandidate.value);
        } else {
            setNamedInsured(undefined);
        }

        refreshData();

    }, [setContactSelected, setNamedInsured, refreshData, namedInsuredCandidates, viewModelService]);

    useEffect(() => {
        if (availableContactSelected === false) {
            onAddInsuredCandidate();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableContactSelected]);

    const formatAvailableValues = (insuredCandidates) => {
        if (!insuredCandidates) {
            return [];
        }
        const formattedValues = insuredCandidates.map((insuredCandidate) => ({
            code: insuredCandidate.publicID,
            name: translator({
                id: insuredCandidate.displayName,
                defaultMessage: insuredCandidate.displayName
            })
        })).sort((a, b) => {
            if (a.name === messages.AddNewInsuredCandidate.defaultMessage) {
                return 1;
            }
            if (b.name === messages.AddNewInsuredCandidate.defaultMessage) {
                return -1;
            }
            return a.name.toUpperCase().localeCompare(b.name.toUpperCase());
        });
        return formattedValues;
    };

    const setDriverName = () => {        
            if(isPrimaryNamedInsured){
                return submissionVM.baseData.primaryNamedInsured_ML.displayName.value 
            } else if(isSecondaryNamedInsured) {
                return submissionVM.baseData.secondaryNamedInsured_ML?.displayName?.value
            }              
    }

    const setDropdownAboveText = () => {   
        if(isPrimaryNamedInsured){
            return "Choose the name of the person who will be the primary named insured on this quote"
        } else if(isSecondaryNamedInsured) {
            return "Choose the name of the person who will be the secondary named insured on this quote"            
        }  
    }     

    const editDriverButtonClick_Ext = useCallback(() => {
        if(namedInsuredCandidates.length <= 0){
            loadNamedInsuredCandidates(isSecondaryNamedInsured);
        } 
        setShowInsuredCandidateDropdown(true);        
    },[]); 

    const overrideProps = {
        insuredCandidateDropdown: {
            availableValues: formatAvailableValues(filteredAvailableInsuredCandidates),
            value: selectedInsuredCandidatePublicID,
            onValueChange: (value) => {
                if (value !== selectedInsuredCandidatePublicID) {
                    onAddInsuredCandidate(value);
                }
            },
            label: label ? translator(label) : undefined,
            visible: showInsuredCandidateDropdown          
        },
        insuredCandidatesSelectionContainer:{
            visible: showInsuredCandidateDropdown  
        },
        nameRow: {
            content: setDriverName()
        },
        driverEditItemLink: {
            onClick: editDriverButtonClick_Ext,
            visible: isNotfromAdditionalIntrestPage
        },
        updateInfoAboveText: {
            content: setDropdownAboveText()
        }

    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={submissionVM}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

MLPolicyNamedInsuredCandidatesComponent.propTypes = {
    submissionVM: PropTypes.shape({}).isRequired,
    loadNamedInsuredCandidates: PropTypes.func
};

export default MLPolicyNamedInsuredCandidatesComponent;
